import { StateRepository } from '@angular-ru/ngxs/decorators';
import { Injectable } from '@angular/core';
import { CollectionNoticeModel, PaginationModel } from '@core/models';
import { CollectionNoticeService } from '@core/services';
import { GenericState, GenericStateModel } from '@core/state/generic.state';
import { MULTI_ITEM_STORE_DEFAULTS } from '@core/state/initial-state';
import { formatMultiStoreData } from '@core/utils';
import { State } from '@ngxs/store';
import { omit } from 'lodash';
import { Observable, finalize, first, map, tap } from 'rxjs';

@StateRepository()
@State<GenericStateModel<CollectionNoticeModel>>({
  name: 'collectionNotice',
  defaults: {
    ...MULTI_ITEM_STORE_DEFAULTS,
  },
})
@Injectable()
export class CollectionNoticeState extends GenericState<CollectionNoticeModel> {
  override service: CollectionNoticeService;
  constructor(private collectionNoticeService: CollectionNoticeService) {
    super();
    this.service = collectionNoticeService;
  }

  public getCollectionNoticeById(
    id: string
  ): Observable<CollectionNoticeModel> {
    if (!(this.snapshot.items[id] as CollectionNoticeModel | undefined)) {
      return this.collectionNoticeService.find(id).pipe(
        tap((collectionNotice) => {
          this.patchState({
            items: {
              ...this.snapshot.items,
              [collectionNotice.id]: collectionNotice,
            },
          });
        })
      );
    }
    return this.state$.pipe(
      first(
        (state) => !!(state.items[id] as CollectionNoticeModel | undefined)
      ),
      map((collectionNoticeState) => {
        return collectionNoticeState.items[id];
      })
    );
  }
  public getAllFromSVByPage(
    params: PaginationModel,
    force = false
  ): Observable<CollectionNoticeModel[]> {
    const page = params.page || 1;
    this.patchState({
      currentPage: params.page,
    });

    if (this.isPageLoaded(page) && !force) {
      return this.itemsByPage$;
    }
    this.patchState({
      isLoading: true,
    });

    return this.service
      .getAllCollectionNotice({
        ...params,
        limit: params.limit ?? this.pageLimit,
      })
      .pipe(
        finalize(() => {
          this.patchState({
            isLoading: false,
          });
        }),
        tap((content): void => {
          const currentState = this.getState();
          const { items } = content.data;
          const currentPages = force ? {} : currentState.pages;
          const objectToPatch: Partial<
            GenericStateModel<CollectionNoticeModel>
          > = {
            totalItems: content.data.total,
            data: omit(content.data, 'items'),
            currentPage: page,
            items: {
              ...currentState.items,
              ...formatMultiStoreData(items),
            },
            pages: {
              ...currentPages,
              [page]: items.map((item) => item.id),
            },
          };
          this.patchState(objectToPatch);
        }),
        map((content) => {
          return content.data.items;
        })
      );
  }
}
